import React, {
  FC,
  createContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import OrderRenewalService, {
  RenewalInfoResponse,
  StartRenewalWorkflowResponse,
} from "@app/service/orderRenewal";

import RenewalInfoDialog from "@app/components/renewalInfoDialog/renewalInfoDialog";
import RenewalPopUp from "@app/components/renewalPopUp/renewalPopUp";
import { useDialog } from "@natera/platform/lib/hooks";
import { useQuery } from "@app/utils";

type GetRenewalInfoProps = {
  orderUidPublic?: string;
  token?: string;
};
export interface OrderRenewalController {
  isLoading: boolean;
  startRenewalWorkflow: () => Promise<StartRenewalWorkflowResponse | undefined>;
  getRenewalInfo: ({
    orderUidPublic,
    token,
  }: GetRenewalInfoProps) => Promise<RenewalInfoResponse | undefined>;
  renewalInfo: RenewalInfoResponse | undefined;
  openRenewalDialog: () => void;
  handleSetPatientInterested: (
    isPatientInterested: boolean,
    isRenewal?: boolean
  ) => Promise<boolean | undefined>;
}

export const Context = createContext<OrderRenewalController>({
  isLoading: false,
  startRenewalWorkflow: () => Promise.reject(),
  getRenewalInfo: () => Promise.reject(),
  renewalInfo: undefined,
  openRenewalDialog: () => undefined,
  handleSetPatientInterested: () => Promise.reject(),
});

Context.displayName = "OrderRenewalContext";

const OrderRenewalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [renewalInfo, setRenewalInfo] = useState<
    RenewalInfoResponse | undefined
  >();

  const query = useQuery();
  const token = query.get("token");

  const [startRenewalWorkflowQuery] = useLazyQuery<{
    startRenewalWorkflow: StartRenewalWorkflowResponse;
  }>(OrderRenewalService.startRenewalWorkflow());

  const startRenewalWorkflow = async () => {
    const result = await startRenewalWorkflowQuery();
    return result.data?.startRenewalWorkflow;
  };

  const [renewalInfoQuery, { loading: isLoadingRenewalInfo }] = useLazyQuery<{
    getRenewalInfo: RenewalInfoResponse;
  }>(OrderRenewalService.getRenewalInfo(), {
    fetchPolicy: "no-cache",
  });

  const [setPatientInterested] = useMutation<{
    setPatientInterested: { isPatientInterested: boolean; isRenewal?: boolean };
  }>(OrderRenewalService.setPatientInterested());

  const [setPatientInterestedRenewalNonAuth] = useMutation<{
    setPatientInterestedRenewalNonAuth: {
      token: string;
      isPatientInterested: boolean;
    };
  }>(OrderRenewalService.setPatientInterestedRenewalNonAuth());

  const getRenewalInfo = async ({
    orderUidPublic,
    token,
  }: GetRenewalInfoProps) => {
    const result = await renewalInfoQuery({
      variables: { orderUidPublic, token },
    });
    if (result.data?.getRenewalInfo) {
      setRenewalInfo(result.data.getRenewalInfo);
    }
    return result.data?.getRenewalInfo;
  };

  const isLoading = useMemo(() => isLoadingRenewalInfo, [isLoadingRenewalInfo]);

  const renewalInfoDialog = useDialog(RenewalInfoDialog);
  const renewalPopUpDialog = useDialog(RenewalPopUp);

  const [renewalInfoDialogWasClosed, setRenewalInfoDialogWasClosed] =
    useState<boolean>(false);

  useEffect(() => {
    renewalInfoDialogWasClosed && openRenewalPopUp();
  }, [renewalInfoDialogWasClosed]);

  const openRenewalPopUp = () => {
    renewalPopUpDialog.open({
      onCloseDialog: renewalPopUpDialog.close,
      limsId: renewalInfo?.clinicLimsId,
      orderUidPublic: renewalInfo?.renewalOrderUidPublic,
    });
  };

  const closeRenewalDialog = () => {
    renewalInfoDialog.close();
    setRenewalInfoDialogWasClosed(true);
  };

  const openRenewalDialog = () => {
    setRenewalInfoDialogWasClosed(false);
    renewalInfoDialog.open({
      onCloseDialog: () => closeRenewalDialog(),
      limsId: renewalInfo?.clinicLimsId,
      orderUidPublic: renewalInfo?.renewalOrderUidPublic,
    });
  };

  const handleSetPatientInterested = async (
    isPatientInterested: boolean,
    isRenewal?: boolean
  ): Promise<boolean | undefined> => {
    try {
      const mutation = token
        ? setPatientInterestedRenewalNonAuth
        : setPatientInterested;

      const variables = token
        ? { token, isPatientInterested }
        : { isPatientInterested, isRenewal };

      const { data } = await mutation({ variables });

      if (!data) {
        return undefined;
      }

      if (token) {
        const result = data as {
          setPatientInterestedRenewalNonAuth: { isPatientInterested: boolean };
        };
        return result.setPatientInterestedRenewalNonAuth?.isPatientInterested;
      } else {
        const result = data as {
          setPatientInterested: {
            isPatientInterested: boolean;
            isRenewal?: boolean;
          };
        };
        return result.setPatientInterested?.isPatientInterested;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error setting patient interest";
      throw new Error(errorMessage);
    }
  };

  const orderRenewalController: OrderRenewalController = useMemo(
    () => ({
      isLoading,
      startRenewalWorkflow,
      getRenewalInfo,
      renewalInfo,
      openRenewalDialog,
      handleSetPatientInterested,
    }),
    [
      isLoading,
      startRenewalWorkflow,
      getRenewalInfo,
      renewalInfo,
      openRenewalDialog,
      handleSetPatientInterested,
    ]
  );

  return (
    <Context.Provider value={orderRenewalController}>
      {renewalInfoDialog.getDialog()}
      {renewalPopUpDialog.getDialog()}
      {children}
    </Context.Provider>
  );
};

export default OrderRenewalProvider;
